import { Cancel, CheckCircle, Info } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { ConditionEntity, MarketType } from '../@types/api';
import TableIconContainer from '../components/atoms/TableIconContainer';
import {
  defaultColumnsCompetitionConditions,
  defaultColumnsMarketConditions,
  defaultColumnsPunterConditions,
  defaultColumnsSportConditions,
  defaultColumnsTournamentConditions,
  defaultColumnsAgentConditions,
} from '../helpers/table';
import { createColumn } from '../utils';
import { getMarketTypeName } from '../helpers';

const commonColumns = {
  id: createColumn('id', 'ID', {
    sortable: false,
    alwaysVisible: true,
  }),
  userId: createColumn('userId', 'ID', { alwaysVisible: true, sortable: false }),
  conditionGroupName: createColumn('conditionGroupName', 'Name', {
    alwaysVisible: true,
    sortable: false,
    valueGetter: (_value, row, _column, _apiRef) => row.conditions?.conditionGroup?.name,
  }),
  description: createColumn('description', 'Description', {
    sortable: false,
    renderCell: ({ row }: GridRenderCellParams<ConditionEntity>) =>
      row.conditions?.conditionGroup?.description && (
        <TableIconContainer>
          <Tooltip title={row.conditions.conditionGroup.description}>
            <Info color='primary' />
          </Tooltip>
        </TableIconContainer>
      ),
  }),
  status: createColumn('isActive', 'Status', {
    renderCell: (params) =>
      params.row.isActive ? (
        <CheckCircle color='primary' fontSize='medium' />
      ) : (
        <Cancel color='error' fontSize='medium' />
      ),
  }),
  sportName: createColumn('sportName', 'Sport', {
    alwaysVisible: true,
    valueGetter: (_value, row, _column, _apiRef) => row?.sport?.name,
  }),
};

const sportColumns: GridColDef[] = [
  commonColumns.id,
  commonColumns.conditionGroupName,
  createColumn('name', 'Sport', { alwaysVisible: true }),
  commonColumns.status,
  commonColumns.description,
];

const competitionColumns: GridColDef[] = [
  commonColumns.id,
  commonColumns.conditionGroupName,
  createColumn('name', 'Competition', { alwaysVisible: true }),
  commonColumns.sportName,
  commonColumns.status,
  commonColumns.description,
];

const tournamentColumns: GridColDef[] = [
  commonColumns.id,
  commonColumns.conditionGroupName,
  createColumn('name', 'Tournament', { alwaysVisible: true }),
  createColumn('competitionName', 'Competition', {
    alwaysVisible: true,
    valueGetter: (_value, row, _column, _apiRef) => row?.competition?.name,
  }),
  createColumn('sportName', 'Sport', {
    alwaysVisible: true,
    valueGetter: (_value, row, _column, _apiRef) => row?.competition?.sport?.name,
  }),
  commonColumns.status,
  commonColumns.description,
];

const agentColumns: GridColDef[] = [
  commonColumns.userId,
  commonColumns.conditionGroupName,
  createColumn('username', 'Username', { alwaysVisible: true }),
  createColumn('email', 'Email'),
  createColumn('fullName', 'Full Name', { sortable: false }),
  commonColumns.status,
  commonColumns.description,
];

const punterColumns: GridColDef[] = [
  commonColumns.userId,
  commonColumns.conditionGroupName,
  createColumn('firstName', 'First Name'),
  createColumn('lastName', 'Last Name'),
  createColumn('username', 'Username', { alwaysVisible: true }),
  commonColumns.status,
  commonColumns.description,
];

const marketColumns: GridColDef[] = [
  commonColumns.id,
  commonColumns.conditionGroupName,
  createColumn('name', 'Market', {
    alwaysVisible: true,
    valueGetter: (_value, row, _column, _apiRef) => getMarketTypeName(row),
  }),
  commonColumns.sportName,
  commonColumns.status,
  commonColumns.description,
];

export const CONDITION_ENTITY_CONFIG = {
  sport: {
    columns: sportColumns,
    defaultVisibleColumns: defaultColumnsSportConditions,
  },
  competition: {
    columns: competitionColumns,
    defaultVisibleColumns: defaultColumnsCompetitionConditions,
  },
  tournament: {
    columns: tournamentColumns,
    defaultVisibleColumns: defaultColumnsTournamentConditions,
  },
  agent: {
    columns: agentColumns,
    defaultVisibleColumns: defaultColumnsAgentConditions,
  },
  punter: {
    columns: punterColumns,
    defaultVisibleColumns: defaultColumnsPunterConditions,
  },
  market_type: {
    columns: marketColumns,
    defaultVisibleColumns: defaultColumnsMarketConditions,
  },
};
